<template>
  <LoadingSpinner :loading='loading'> </LoadingSpinner>

  <div class="container mt-4 mb-4">
    <div style="max-width:560px">
        <h3>Tripletex konto</h3>
        <div v-if='updated'>
          <span class='message-info'>Tripletex kontoinnstillingene er oppdatert</span>
        </div>

        <div v-if="locations.length === 0 && !loading" class="mt-4">
          <span class="message-info">Ingen avdelinger har Tripletex aktivert. Aktiver det under Avdelinger.</span>
        </div>

        <div v-if="locations.length > 0 && hasMultiLocations()" class="select-location-box mt-4">
          <span class="ingredient-title">For avdeling</span>
          <select class="form-select" v-model="locationId" @change="getTripletexAccount()">
            <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
          </select>
        </div>

        <template v-if="locations.length > 0">
          <!-- Locked when this location points at the LIVE Tripletex. Tokens and the environment switch
               decide where a merchant's real bookkeeping goes; a test connection stays editable. -->
          <config-lock ref="lock" :configured="isProduction">
          <div class="mt-4">
            <span class='ingredient-title'>API URL</span>
            <select class="form-select" v-model="tripletexAccount.baseApiUrl">
              <option value="https://api-test.tripletex.tech/v2">TEST (api-test.tripletex.tech)</option>
              <option value="https://tripletex.no/v2">PROD (tripletex.no)</option>
            </select>
          </div>

          <div class="mt-3">
            <span class='ingredient-title'>Consumer ID</span>
            <input class='form-control' type='text' v-model='tripletexAccount.consumerId' />
          </div>

          <div>
            <span class='ingredient-title'>Consumer Token</span>
            <input class='form-control' type='text' v-model='tripletexAccount.consumerToken' />
          </div>

          <div>
            <span class='ingredient-title'>Employee Token</span>
            <input class='form-control' type='text' v-model='tripletexAccount.employeeToken' />
          </div>

          <div class='bo_hr mt-4'>
            <hr/>
          </div>

          <div class='d-flex gap-3 mb-3'>
            <button v-if='activateSaveButton' type='button' class='bo_btn' @click='save'> Lagre </button>
            <button v-else type='button' class='bo_btn' @click='save' disabled='true'> Lagre </button>
          </div>
          </config-lock>
          <!-- Outside the lock, deliberately. Testing a connection reads; it changes nothing. Making
               someone unlock a production config to check whether it still works would teach exactly the
               reflex the lock exists to prevent. -->
          <div class='d-flex gap-3 mb-3'>
            <button type='button' class='bo_btn' @click='testConnection'> Test tilkobling </button>
          </div>
          <div v-if="connectionTested" class="mb-5">
            <span :class="connectionSuccess ? 'message-success' : 'message-error'">{{ connectionMessage }}</span>
          </div>
        </template>
    </div>
  </div>
</template>

<script>
  import SettingsService from '@/services/settings/SettingsService';
  import MerchantUtil from "@/util/MerchantUtil";
  import LoadingSpinner from "@/components/other/LoadingSpinner";
  import LocationUtil from "@/util/LocationUtil";
  import LocationService from "@/services/location/LocationService";
  import ConfigLock from "@/components/other/ConfigLock.vue";
  import AccountingEnv from "@/util/AccountingEnv";

  export default {
    components: {LoadingSpinner, ConfigLock},
    data() {
      return {
        loading: true,
        locationId: '',
        locations: [],

        updated: false,
        connectionTested: false,
        connectionSuccess: false,
        connectionMessage: '',

        tripletexAccount: {
          type: "tripletex_account",
          merchant: '',
          locationId: '',
          consumerToken: '',
          employeeToken: '',
          consumerId: '',
          baseApiUrl: 'https://api-test.tripletex.tech/v2'
        }
      };
    },
    mounted() {
      LocationService.listLocations().then(
          (response) => {
            this.locations = response.data.filter(l => l.accountingSystem === 'TRIPLETEX');
            this.locationId = this.locations.length ? this.locations[0].id : '';

            if (this.locationId) {
              this.getTripletexAccount();
            } else {
              this.loading = false;
            }
          },
          (error) => {
            console.log(error);
          }
      );
    },
    computed: {
      isProduction(){
        return AccountingEnv.isProduction(this.tripletexAccount.baseApiUrl);
      },
      activateSaveButton(){
        if(this.tripletexAccount.consumerToken.length > 0 &&
            this.tripletexAccount.employeeToken.length > 0 &&
            this.tripletexAccount.consumerId.length > 0){
          return true;
        } else {
          return false;
        }
      }
    },
    methods: {
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      getTripletexAccount(){
        SettingsService.getTripletexAccount(this.locationId).then(
            (response) => {
              if(response.data !== null){
                this.tripletexAccount = response.data;
              }

              this.loading = false;
              // Each location is its own connection: an unlock for one must not carry to the next.
              this.$nextTick(() => this.$refs.lock && this.$refs.lock.reset());
            },
            (error) => {
              console.log(error);
            }
        );
      },
      testConnection(){
        this.loading = true;
        this.connectionTested = false;
        SettingsService.testTripletexConnection(this.locationId).then(
            (response) => {
              this.connectionTested = true;
              this.connectionSuccess = response.data.success;
              this.connectionMessage = response.data.message;
              this.loading = false;
            },
            (error) => {
              this.connectionTested = true;
              this.connectionSuccess = false;
              this.connectionMessage = 'Feil ved tilkobling';
              console.log(error);
              this.loading = false;
            }
        );
      },
      save(){
        this.loading = true;
        this.tripletexAccount.merchant = MerchantUtil.getMerchant();
        this.tripletexAccount.locationId = this.locationId;

        SettingsService.editTripletexAccount(this.tripletexAccount).then(
            (response) => {
              let documentId = response.data;
              this.tripletexAccount.id = documentId;
              if(documentId){
                this.updated = true;
              } else {
                console.log("Editing tripletexAccount failed");
              }
              this.loading = false;
              // Back to the lock the SAVED config calls for: production re-locks, test stays open.
              this.$nextTick(() => this.$refs.lock && this.$refs.lock.reset());
            },
            (error) => {
              console.log(error);
            }
        );
      }
    }
  };
</script>
